import { ref, computed, watch } from 'vue';

export function useLevellingColumns({ data }) {
  const self = {
    dtColumnsEnabled: ref([]),
    dtColumnsOrdered: ref([]),
    addFilterMenu: ref(false),
  };

  // The evaluator column field resolves asynchronously (see useLevellingData), often
  // after initColumns has already snapshotted the fallback token. When it changes, swap
  // the stale token in the enabled/ordered sets so the column keeps its place/visibility.
  watch(() => data.evaluatorColumnField.value, (newField, oldField) => {
    if (!oldField || newField === oldField) return;
    self.dtColumnsEnabled.value = self.dtColumnsEnabled.value.map(v => v === oldField ? newField : v);
    self.dtColumnsOrdered.value = self.dtColumnsOrdered.value.map(o =>
      o.column === oldField ? { ...o, column: newField } : o
    );
  });

  // --- Computed ---
  self.allAvailableColumns = computed(() => {
    const cols = [
      { text: 'Level', value: 'level', width: "150px" },
      { text: 'Evaluator', value: data.evaluatorColumnField.value, width: "160px", isPersonColumn: true },
      { text: 'Reference', value: 'system_number', width: "140px" },
      { text: 'Job Title', value: 'doc_name' },
      { text: 'Doc Type', value: 'doc_type', width: "140px" },
    ];
    data.hierarchyLevelColumns.value.forEach(hc => {
      cols.push({ text: hc.label, value: hc.field });
    });
    (data.classifierTypes.value || []).forEach(ct => {
      cols.push({ text: ct.ct_name, value: 'classifier_' + ct.ct_id });
    });
    const existing = new Set(cols.map(c => c.value));
    (data.config.value?.docListColumns || []).forEach(c => {
      if (!existing.has(c.value)) {
        cols.push({ text: c.text, value: c.value, isDate: c.isDate, isPersonColumn: c.isPersonColumn });
      }
    });
    return cols;
  });

  self.defaultColumnValues = computed(() => {
    const vals = ['level', data.evaluatorColumnField.value, 'system_number', 'doc_name', 'doc_type'];
    data.hierarchyLevelColumns.value.forEach(hc => vals.push(hc.field));
    (data.classifierTypes.value || []).forEach(ct => vals.push('classifier_' + ct.ct_id));
    return vals;
  });

  self.extraColumns = computed(() => {
    const defaults = new Set(self.defaultColumnValues.value);
    return self.dtColumnsEnabled.value
      .filter(v => !defaults.has(v))
      .map(v => {
        const col = self.allAvailableColumns.value.find(c => c.value === v);
        if (!col) return null;
        return { text: col.text, value: col.value, isDate: col.isDate, isPersonColumn: col.isPersonColumn };
      })
      .filter(Boolean);
  });

  self.customiseColumns = computed(() => {
    const includeCount = self.dtColumnsOrdered.value.filter(x =>
      self.allAvailableColumns.value.some(c => c.value === x.column)
    ).length;

    let cols = self.allAvailableColumns.value.map((c, ci) => {
      const ord = self.dtColumnsOrdered.value.find(x => x.column === c.value);
      return {
        value: c.value,
        text: c.text,
        position: ord ? ord.position : 1000 + ci,
        included: !!ord,
      };
    });
    cols.sort((a, b) => a.position - b.position);
    cols = cols.map((c, i) => ({
      ...c,
      position: i,
      allowUp: c.included && i > 0,
      allowDown: c.included && i < includeCount - 1,
    }));
    return cols;
  });

  self.tableColumns = computed(() => {
    const cols = [];
    const ordered = [...self.dtColumnsOrdered.value].sort((a, b) => a.position - b.position);
    ordered.forEach(o => {
      if (!self.dtColumnsEnabled.value.includes(o.column)) return;
      const def = self.allAvailableColumns.value.find(c => c.value === o.column);
      if (def) {
        // The evaluator column resolves to a different role field per doc type, so it
        // can't be sorted by a single field server-side (index mode) — disable sorting.
        const sortable = o.column !== data.evaluatorColumnField.value;
        cols.push({ text: def.text, value: def.value, sortable, ...(def.width && { width: def.width }) });
      }
    });
    return cols;
  });

  // --- Methods ---
  self.initColumns = () => {
    self.dtColumnsEnabled.value = [...self.defaultColumnValues.value];
    self.dtColumnsOrdered.value = self.dtColumnsEnabled.value.map((v, i) => ({ column: v, position: i }));
  };

  self.addRemoveColumn = () => {
    const added = self.dtColumnsEnabled.value.find(x => !self.dtColumnsOrdered.value.some(o => o.column === x));
    if (added) {
      self.dtColumnsOrdered.value = [...self.dtColumnsOrdered.value, { column: added, position: self.dtColumnsOrdered.value.length }];
    } else {
      const removed = self.dtColumnsOrdered.value.findIndex(x => !self.dtColumnsEnabled.value.some(o => o === x.column));
      if (removed >= 0) {
        const removedCol = self.dtColumnsOrdered.value[removed].column;
        const updated = [...self.dtColumnsOrdered.value];
        updated.splice(removed, 1);
        updated.forEach((x, xi) => x.position = xi);
        self.dtColumnsOrdered.value = updated;
        data.removeExtraColumnFilter(removedCol);
      }
    }
    data.doFilter();
  };

  self.moveColumn = (column, change) => {
    const ordered = [...self.dtColumnsOrdered.value];
    const current = ordered.findIndex(c => c.column === column.value);
    if (current < 0) return;
    ordered[current].position += change;
    ordered[current + change].position -= change;
    ordered.sort((a, b) => a.position - b.position);
    self.dtColumnsOrdered.value = ordered;
  };

  self.resetColumnsToDefault = () => {
    const defaults = new Set(self.defaultColumnValues.value);
    Object.keys(data.extraColumnFilters.value).forEach(k => {
      if (!defaults.has(k)) data.removeExtraColumnFilter(k);
    });
    self.initColumns();
    data.doFilter();
  };

  return self;
}
